"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BandwidthEstimator = void 0;
var vpt_abr_common_1 = require("@sky-uk-ott/vpt-abr-common");
var sample_aggregator_1 = require("./aggregators/sample-aggregator");
var network_estimation_constants_1 = require("./network-estimation-constants");
var create_smoother_1 = require("./smoothers/create-smoother");
var create_statistical_aggregator_1 = require("./statistical-aggregators/create-statistical-aggregator");
var utils_1 = require("./utils");
var getSampleValue = function (sample) { return sample.bandwidthEstimateBitsPerS; };
var getSampleWeight = function (sample) { return sample.timeSpentDownloadingMs; };
var BandwidthEstimator = /** @class */ (function () {
    function BandwidthEstimator(logger, config, getStabilisedBufferAheadSeconds) {
        if (config === void 0) { config = {}; }
        this.getStabilisedBufferAheadSeconds = getStabilisedBufferAheadSeconds;
        this.sampleHandlerId = 0;
        this.bytesSampled = 0;
        this.aggregatedSamples = [];
        this.sampleHandlers = new Map();
        this.segmentDurationSeconds = 0;
        this.isDestroyed = false;
        this.logger = logger.withContext('BandwidthEstimator');
        this.logger.verbose('Received config: ', config);
        this.sampleAggregator = new sample_aggregator_1.SampleAggregator(this.logger);
        this.config = __assign({ sampleRate: network_estimation_constants_1.DEFAULT_SAMPLE_RATE, minTotalBytes: network_estimation_constants_1.DEFAULT_MIN_BYTES, defaultBandwidthEstimate: network_estimation_constants_1.DEFAULT_BANDWIDTH_ESTIMATE, minBytesForSample: network_estimation_constants_1.DEFAULT_MIN_BYTES_FOR_SAMPLE, minSampleDurationMs: network_estimation_constants_1.DEFAULT_MIN_SAMPLE_DURATION_MS, numberOfStoredSamples: network_estimation_constants_1.DEFAULT_STORED_ESTIMATOR_SAMPLES }, config);
        this.defaultEstimate = this.config.defaultBandwidthEstimate;
        this.logger.info('Using config: ', this.config);
    }
    BandwidthEstimator.prototype.reinitialise = function (config) {
        var oldSampleRate = this.config.sampleRate;
        this.config = __assign(__assign({}, this.config), config);
        if (this.intervalId && oldSampleRate !== this.config.sampleRate) {
            this.clearInterval();
            this.startInterval(this.config.sampleRate);
        }
    };
    BandwidthEstimator.prototype.sample = function (timeToDownloadMs, timeRecorded, numBytes) {
        if (timeToDownloadMs < this.config.minSampleDurationMs || numBytes < this.config.minBytesForSample) {
            this.logger.verbose('Sample discarded (criteria not met)', timeToDownloadMs, numBytes, timeRecorded);
            return;
        }
        this.bytesSampled += numBytes;
        this.logger.verbose('Sample received', timeToDownloadMs, numBytes, timeRecorded);
        this.sampleAggregator.sample(timeToDownloadMs, numBytes, timeRecorded);
        if (!this.intervalId) {
            this.startInterval(this.config.sampleRate);
        }
    };
    /**
     * sampleDirect is useful for inputting estimates that come from elsewhere
     */
    BandwidthEstimator.prototype.sampleDirect = function (bandwidthEstimateBitsPerS, timeRecorded) {
        this.submitSample({ bandwidthEstimateBitsPerS: bandwidthEstimateBitsPerS, timeRecorded: timeRecorded });
        this.bytesSampled = this.config.minTotalBytes; // We don't know how big the download was, so we should assume it's enough
    };
    BandwidthEstimator.prototype.getSmoother = function (smootherConfig) {
        var _this = this;
        var smoother = (0, create_smoother_1.createSmoother)(this.logger, smootherConfig, getSampleValue, this.getDynamicMaxSampleAgeMs.bind(this), getSampleWeight);
        var unregister = this.registerSampleHandler(smoother);
        return {
            getValue: function () {
                _this.checkIsDestroyed();
                return _this.getEstimate(smoother);
            },
            unregister: unregister,
        };
    };
    BandwidthEstimator.prototype.getStatisticalAggregator = function (statisticalAggregatorConfig) {
        var _this = this;
        var aggregator = (0, create_statistical_aggregator_1.createStatisticalAggregator)(this.logger, statisticalAggregatorConfig, getSampleValue);
        var unregister = this.registerSampleHandler(aggregator);
        return {
            getValue: function () {
                _this.checkIsDestroyed();
                return aggregator.getValue();
            },
            unregister: unregister,
        };
    };
    BandwidthEstimator.prototype.setSegmentDurationSeconds = function (segmentDurationSeconds) {
        this.segmentDurationSeconds = segmentDurationSeconds;
    };
    BandwidthEstimator.prototype.registerSampleHandler = function (sampleHandler) {
        var _this = this;
        var id = this.sampleHandlerId++;
        this.aggregatedSamples.forEach(function (s) { return sampleHandler.sample(s); });
        this.sampleHandlers.set(id, sampleHandler);
        return function () { return _this.sampleHandlers.delete(_this.sampleHandlerId); };
    };
    BandwidthEstimator.prototype.getDynamicMaxSampleAgeMs = function (configuredMaxSampleAgeMs) {
        if (!this.getStabilisedBufferAheadSeconds) {
            return configuredMaxSampleAgeMs;
        }
        var bufferSeconds = Math.max(this.getStabilisedBufferAheadSeconds() - this.segmentDurationSeconds, this.segmentDurationSeconds);
        var bufferMs = bufferSeconds * 1000;
        return Math.min(bufferMs, configuredMaxSampleAgeMs);
    };
    BandwidthEstimator.prototype.submitSample = function (sample) {
        this.aggregatedSamples = __spreadArray(__spreadArray([], this.aggregatedSamples, true), [sample], false);
        this.aggregatedSamples = vpt_abr_common_1.ArrayUtils.lastItems(this.aggregatedSamples, this.config.numberOfStoredSamples);
        this.sampleHandlers.forEach(function (sampleHandler) {
            sampleHandler.sample(sample);
        });
    };
    BandwidthEstimator.prototype.generateEstimates = function () {
        if (!this.hasGoodEstimate()) {
            return;
        }
        var aggregatedSample = this.sampleAggregator.getAggregatedSample();
        this.sampleAggregator.clearSamples();
        if (!aggregatedSample) {
            this.logger.verbose('Aggregated value not defined');
            return;
        }
        var estimateBytesPerMs = aggregatedSample.estimateBytesPerMs, timeRecorded = aggregatedSample.timeRecorded, timeSpentDownloadingMs = aggregatedSample.timeSpentDownloadingMs;
        var estimateBitsPerS = (0, utils_1.convertBytesPerMsToBitsPerS)(estimateBytesPerMs);
        this.logger.verbose("Aggregated value: ".concat(estimateBitsPerS));
        this.submitSample({ bandwidthEstimateBitsPerS: estimateBitsPerS, timeRecorded: timeRecorded, timeSpentDownloadingMs: timeSpentDownloadingMs });
    };
    BandwidthEstimator.prototype.hasGoodEstimate = function () {
        return this.bytesSampled >= this.config.minTotalBytes;
    };
    BandwidthEstimator.prototype.forceUpdate = function () {
        this.generateEstimates();
    };
    BandwidthEstimator.prototype.getEstimate = function (smoother) {
        var _a;
        return (_a = smoother.getValue()) !== null && _a !== void 0 ? _a : this.defaultEstimate;
    };
    BandwidthEstimator.prototype.checkIsDestroyed = function () {
        if (this.isDestroyed) {
            this.logger.error('ERROR: `getEstimate` called on a destroyed Bandwidth Estimator');
        }
    };
    BandwidthEstimator.prototype.startInterval = function (intervalMs) {
        var _this = this;
        this.generateEstimates();
        this.intervalId = self.setInterval(function () {
            _this.generateEstimates();
        }, intervalMs);
    };
    BandwidthEstimator.prototype.clearInterval = function () {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    };
    BandwidthEstimator.prototype.clearAllSamples = function () {
        this.aggregatedSamples = [];
        this.bytesSampled = 0;
        this.sampleAggregator.clearSamples();
        this.sampleHandlers.forEach(function (aggregator) {
            aggregator.clear();
        });
    };
    BandwidthEstimator.prototype.clear = function () {
        this.clearAllSamples();
    };
    BandwidthEstimator.prototype.destroy = function () {
        this.clearAllSamples();
        this.clearInterval();
        this.sampleHandlers.clear();
        this.isDestroyed = true;
    };
    return BandwidthEstimator;
}());
exports.BandwidthEstimator = BandwidthEstimator;
